// src/components/mdx/client/visualizations/MarketingFunnelChart.client.tsx

/**
 * Styled conversion funnel for the chart selection explorer.
 *
 * Uses custom trapezoid stages (not a generic horizontal bar chart) so the
 * visualization reads immediately as a funnel with labeled drop-offs.
 */

'use client'

import { useMemo } from 'react'

import { MARKETING_DATA_VIZ_SERIES } from '~/components/mdx/client/visualizations/blogChartPalette'
import {
  generateMarketingFunnelChartStages,
  type FunnelStagePoint
} from '~/components/mdx/client/visualizations/marketingDataVizData'
import { cn } from '~/lib/utils'

const STAGE_COLORS = [
  MARKETING_DATA_VIZ_SERIES.funnel,
  MARKETING_DATA_VIZ_SERIES.primary,
  MARKETING_DATA_VIZ_SERIES.secondary,
  MARKETING_DATA_VIZ_SERIES.accent
] as const

/**
 * Props for {@link MarketingFunnelChart}.
 *
 * @typedef {Object} MarketingFunnelChartProps
 * @property {FunnelStagePoint[]} [data] - Optional funnel stages; defaults to sample data.
 * @property {boolean} [isActive=true] - Whether entrance styling should apply.
 */
export interface MarketingFunnelChartProps {
  data?: FunnelStagePoint[]
  isActive?: boolean
}

/**
 * Computes stage-to-stage conversion and drop-off percentages.
 *
 * @param {FunnelStagePoint} current - Current funnel stage.
 * @param {FunnelStagePoint} next - Following funnel stage.
 * @returns {{ convertPct: number; dropPct: number }} Transition metrics.
 */
function computeTransition(
  current: FunnelStagePoint,
  next: FunnelStagePoint
): { convertPct: number; dropPct: number } {
  const convertPct = (next.count / current.count) * 100
  return { convertPct, dropPct: 100 - convertPct }
}

/**
 * Renders a narrowing conversion funnel with drop-off callouts between stages.
 *
 * @param {MarketingFunnelChartProps} props - Chart props.
 * @returns {JSX.Element} Styled funnel visualization.
 */
export function MarketingFunnelChart({ data, isActive = true }: MarketingFunnelChartProps): JSX.Element {
  const stages = useMemo(() => data ?? generateMarketingFunnelChartStages(), [data])
  const topCount = stages[0]?.count ?? 1

  return (
    <div className="flex h-full flex-col justify-center gap-3 py-2" aria-label="Marketing conversion funnel from clicks through purchases">
      <ul className="sr-only">
        {stages.map((stage, index) => {
          const nextStage = stages[index + 1]
          const transition = nextStage ? computeTransition(stage, nextStage) : null
          return (
            <li key={stage.stage}>
              {stage.stage}: {stage.count.toLocaleString()}
              {index === 0 ? ' top of tracked funnel' : `, ${((stage.count / topCount) * 100).toFixed(1)}% of clicks remain`}
              {transition ? `; next stage converts ${transition.convertPct.toFixed(1)}% and loses ${transition.dropPct.toFixed(0)}%` : ''}
            </li>
          )
        })}
      </ul>
      {stages.map((stage, index) => {
        const nextStage = stages[index + 1]
        const widthPct = Math.max(3, Math.min(100, (stage.count / topCount) * 100))
        const color = STAGE_COLORS[index % STAGE_COLORS.length] ?? MARKETING_DATA_VIZ_SERIES.funnel
        const transition = nextStage ? computeTransition(stage, nextStage) : null
        const isMajorLeak = transition !== null && transition.convertPct <= 20

        return (
          <div key={stage.stage} className="w-full space-y-2">
            <div
              className={cn(
                'relative overflow-hidden rounded-xl border bg-slate-950/40 px-4 py-3 shadow-[0_10px_30px_rgba(0,0,0,0.28)] sm:px-5 sm:py-3.5',
                isMajorLeak ? 'border-rose-400/35' : 'border-white/10',
                isActive ? 'opacity-100' : 'opacity-90'
              )}
            >
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-1.5 bg-white/5"
              />
              <div
                aria-hidden="true"
                className="absolute bottom-0 left-0 h-1.5 transition-all duration-700 ease-out"
                style={{
                  width: `${widthPct}%`,
                  background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`
                }}
              />
              <div className="relative z-10 flex items-center justify-between gap-3">
                <div className="min-w-0 text-left">
                  <p className="truncate text-xs font-semibold text-white sm:text-sm">{stage.stage}</p>
                  <p className="text-[10px] text-white/75 sm:text-xs">
                    {index === 0
                      ? 'Top of tracked funnel'
                      : `${((stage.count / topCount) * 100).toFixed(1)}% of clicks remain`}
                  </p>
                </div>
                <p className="shrink-0 text-base font-bold tabular-nums text-white sm:text-lg">
                  {stage.count.toLocaleString()}
                </p>
              </div>
            </div>

            {transition !== null && (
              <div className="flex items-center justify-center">
                <div
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] sm:text-xs',
                    isMajorLeak
                      ? 'border-rose-400/30 bg-rose-500/10 text-rose-200'
                      : 'border-white/10 bg-white/5 text-gray-400'
                  )}
                >
                  <span aria-hidden="true">↓</span>
                  <span>
                    {transition.convertPct.toFixed(1)}% convert · {transition.dropPct.toFixed(0)}% lost
                  </span>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default MarketingFunnelChart
